/**
 * A data widget's numbers as a spreadsheet sheet, as AgencyAnalytics' XLS
 * download does: raw values (not "70.2K"), one row per item or period, no
 * totals row. Rand amounts are real numbers in Rand (stored as cents), and
 * percentages stay fractions shown as % (so 0.42 reads 42.0%, never 0.42%).
 */

import type { CatalogueMetric, CatalogueSource, Widget, WidgetResult } from './reports-api.ts';
import type { Cell, Sheet } from './xlsx.ts';

const CENTS_PER_RAND = 100;
const TOTALS_TYPES = new Set(['METRIC', 'GOAL', 'GAUGE', 'SPARKLINE']);

function cellOf(value: number | null | undefined, metric: CatalogueMetric): Cell {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  switch (metric.format) {
    case 'money':
      return { value: value / CENTS_PER_RAND, format: 'money' };
    case 'percent':
      return { value, format: 'percent' };
    case 'number':
      return { value, format: Number.isInteger(value) ? 'integer' : 'decimal' };
    default:
      return { value, format: 'decimal' };
  }
}

function metricsOf(widget: Widget, source: CatalogueSource | undefined): CatalogueMetric[] {
  return (widget.data.metrics ?? [])
    .map((key) => source?.metrics.find((metric) => metric.key === key))
    .filter((metric): metric is CatalogueMetric => Boolean(metric));
}

/** "Clicks (previous)" columns appear only when the result carries a comparison. */
function headerWith(first: string, metrics: CatalogueMetric[], withPrevious: boolean): Cell[] {
  return [
    first,
    ...metrics.flatMap((metric) =>
      withPrevious ? [metric.label, `${metric.label} (previous)`] : [metric.label],
    ),
  ];
}

function tableRows(result: WidgetResult, metrics: CatalogueMetric[], dimension: string): Cell[][] {
  const rows = result.rows ?? [];
  const withPrevious = rows.some((row) => row.compare !== null);
  return [
    headerWith(dimension, metrics, withPrevious),
    ...rows.map((row) => [
      row.label,
      ...metrics.flatMap((metric) =>
        withPrevious
          ? [cellOf(row.values[metric.key], metric), cellOf(row.compare?.[metric.key], metric)]
          : [cellOf(row.values[metric.key], metric)],
      ),
    ]),
  ];
}

function seriesRows(result: WidgetResult, metrics: CatalogueMetric[]): Cell[][] {
  return [
    ['Date', ...metrics.map((metric) => metric.label)],
    ...result.series.map((point) => [
      result.granularity === 'month' ? point.bucket.slice(0, 7) : point.bucket.slice(0, 10),
      ...metrics.map((metric) => cellOf(point.values[metric.key], metric)),
    ]),
  ];
}

function totalRows(result: WidgetResult, metrics: CatalogueMetric[]): Cell[][] {
  return [
    ['Metric', 'Value', 'Previous', 'Change'],
    ...metrics.map((metric) => {
      const entry = result.metrics.find((item) => item.key === metric.key);
      const change = entry?.change?.percent;
      return [
        metric.label,
        cellOf(entry?.value, metric),
        cellOf(entry?.previous, metric),
        change === null || change === undefined
          ? null
          : { value: change, format: 'percent' as const },
      ];
    }),
  ];
}

/** The sheet for one data widget, or null for content widgets and widgets with no numbers yet. */
export function widgetSheet(
  widget: Widget,
  result: WidgetResult | undefined,
  source: CatalogueSource | undefined,
): Sheet | null {
  const metrics = metricsOf(widget, source);
  if (!result || result.state !== 'OK' || metrics.length === 0) return null;
  const name = widget.display.title || `${source?.label ?? 'Data'} ${widget.type.toLowerCase()}`;
  if (result.rows && widget.data.dimension) {
    const dimension =
      source?.dimensions.find((item) => item.key === widget.data.dimension)?.label ??
      (widget.data.dimension === 'client' ? 'Client' : 'Name');
    return { name, rows: tableRows(result, metrics, dimension) };
  }
  if (!TOTALS_TYPES.has(widget.type) && result.series.length > 0)
    return { name, rows: seriesRows(result, metrics) };
  return { name, rows: totalRows(result, metrics) };
}

/** A tidy file name: "TailorMe – Marketing overview – 2026-09-01 to 2026-09-30". */
export function sheetFileName(parts: string[]): string {
  return parts
    .filter(Boolean)
    .join(' – ')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .trim();
}
